import { FileRepresentation } from '@/components/form-builder/form-builder.types';
import { MimeType } from '@/types';
import { fileUtils } from '@/utils/file.utils';
import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, map, Observable, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';

interface FileData {
  file: string;
  fileContent?: string;
}

export enum NAMESPACE_ENUM {
  AGPM_MODEL = 'model-agpm',
  AGPM = 'agpm',
  PPM_MODEL = 'model-ppm',
  PPM = 'ppm',
  PAA_MODEL = 'model-paa',
  PAA = 'paa',
  DAC_MODEL = 'model-dac',
  DAC = 'dac',
  REQUESTS = 'demande',
  MEETING_REQUESTS = 'meeting-demande',
  REPORT = 'report',
  MEETING_REPORT_FOR_MEETING = 'meeting-report',
  MEETING_PPM = 'meeting-ppm',
  MEETING_DAC = 'meeting-dac',
  MEETING_PPM_FOR_MEETING = 'meeting-ppm-for-meeting',
  MEETING_DAC_FOR_MEETING = 'meeting-dac-for-meeting',
  PV = 'pv',
  SNIPPET = 'extrait',
  OPINION_PPM = 'avis-ppm',
  OPINION_DAC = 'avis-dac',
  OPINION_REPORT = 'avis-report',
  OPINION_REQUEST = 'avis-demande',
}

@Injectable({
  providedIn: 'root',
})
export class FileService {
  httpClient = inject(HttpClient);

  BASEURL = `${environment.api.paramsUrl}/docs/download`;

  download(file: FileRepresentation, nameSpace?: NAMESPACE_ENUM): Observable<FileData> {
    const url = nameSpace ? `${this.BASEURL}/${nameSpace}` : this.BASEURL;
    const response = this.httpClient.post<FileData>(url, {
      id: file.alfresco_id,
    });
    response.subscribe({
      next: data => {
        const content = data.file || data.fileContent;
        if (!content) return;
        const blob = new Blob([fileUtils.decodeBase64(content)], { type: file.extension });
        fileUtils.triggerBlobDownload(blob, `${file.filename}.${file.extension}`);
      },
      error: err => {
        console.error('Download failed', err);
      },
    });
    return response;
  }

  openFile(file: FileRepresentation, nameSpace?: NAMESPACE_ENUM): Observable<FileData> {
    const url = nameSpace ? `${this.BASEURL}/${nameSpace}` : this.BASEURL;
    const response = this.httpClient.post<FileData>(url, {
      id: file.alfresco_id,
    });
    response.subscribe({
      next: data => {
        fileUtils.triggerNavigatorOpen(file, data.file);
      },
      error: err => {
        console.error('Open file failed', err);
      },
    });
    return response;
  }

  private getMimeType(extension: string): string {
    const key = extension.toUpperCase() as keyof typeof MimeType;
    return MimeType[key] || MimeType.OCTET_STREAM;
  }

  getFile(file: FileRepresentation, nameSpace?: NAMESPACE_ENUM): Observable<File | null> {
    const url = nameSpace ? `${this.BASEURL}/${nameSpace}` : this.BASEURL;
    return this.httpClient.post<{ file: string }>(url, { id: file.alfresco_id }).pipe(
      map(response => {
        const binary = atob(response.file);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) {
          bytes[i] = binary.charCodeAt(i);
        }

        if (!file.extension) {
          console.warn('File extension is missing');
          return null;
        }
        const mimeType = this.getMimeType(file.extension);

        return new File([bytes], `${file.filename}.${file.extension}`, {
          type: mimeType,
        });
      })
    );
  }

  getUrlFromAlfrescoObject(file: FileRepresentation, nameSpace?: NAMESPACE_ENUM): Observable<Uint8Array> {
    const url = nameSpace ? `${this.BASEURL}/${nameSpace}` : this.BASEURL;
    return this.httpClient.post<{ file: string }>(url, { id: file.alfresco_id }).pipe(
      map(response => fileUtils.decodeBase64(response.file)),
      catchError(err => {
        console.error('Download failed', err);
        return throwError(() => err);
      })
    );
  }
}
